/**
 * <s-exit-popup>: exit-intent WhatsApp offer popup.
 *
 * - Opens once per 7 days when the shopper looks like they are leaving:
 *   desktop = pointer leaves through the top of the window,
 *   phones = a fast scroll back up after reading at least 35% of the page.
 * - Never auto-opens in the theme editor; `#aaji-offer` in the URL opens it for testing.
 * - The form posts natively to Shopify's contact form (number arrives in the store
 *   email inbox). After the redirect (?contact_posted=true) the success panel shows
 *   the code, and a reminder pill keeps the code + countdown handy until it ends.
 *   The countdown is only a reminder; it does not expire the discount.
 */
const SEEN_KEY = 'aaji-exit-popup-seen';
const CLAIMED_KEY = 'aaji-exit-popup-claimed';
const PENDING_KEY = 'aaji-exit-popup-pending';
const SNOOZE_MS = 7 * 24 * 60 * 60 * 1000;

const read = (key) => {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
};
const write = (key, value) => {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    /* storage blocked: popup still works for this page view */
  }
};

class ExitPopup extends HTMLElement {
  #abort = new AbortController();
  #timer = 0;
  #intent = new AbortController();

  connectedCallback() {
    const { signal } = this.#abort;
    this.dialog = this.querySelector('dialog');
    this.formStep = this.querySelector('[data-step="form"]');
    this.successStep = this.querySelector('[data-step="success"]');
    this.reminder = this.querySelector('[data-reminder]');
    this.minutes = Number(this.dataset.minutes) || 15;
    if (!this.dialog) return;

    this.querySelectorAll('[data-code]').forEach((el) => {
      el.textContent = this.dataset.code;
    });
    this.querySelectorAll('[data-apply-link]').forEach((el) => {
      el.setAttribute('href', this.dataset.applyUrl);
    });

    const form = this.querySelector('form');
    form?.addEventListener(
      'submit',
      () => {
        const input = form.querySelector('input[type="tel"]');
        if (input) input.value = input.value.replace(/\D/g, '').slice(-10);
        write(PENDING_KEY, String(Date.now()));
      },
      { signal }
    );

    this.querySelector('[data-copy]')?.addEventListener('click', (event) => this.#copy(event.currentTarget), { signal });

    this.dialog.addEventListener(
      'click',
      (event) => {
        if (event.target === this.dialog) this.dialog.close();
      },
      { signal }
    );

    const params = new URLSearchParams(window.location.search);
    const pending = Number(read(PENDING_KEY));
    if (params.get('contact_posted') === 'true' && pending && Date.now() - pending < 10 * 60 * 1000) {
      write(PENDING_KEY, '');
      write(CLAIMED_KEY, String(Date.now()));
      write(SEEN_KEY, String(Date.now()));
      this.#showSuccess();
      this.#open();
      return;
    }

    if (this.#claimedAt()) {
      this.#showSuccess();
      return;
    }

    if (window.location.hash === '#aaji-offer') {
      this.#open();
      return;
    }

    if (this.hasAttribute('data-design-mode')) return;
    const seen = Number(read(SEEN_KEY));
    if (seen && Date.now() - seen < SNOOZE_MS) return;
    if (/\/(cart|checkout|account)/.test(window.location.pathname)) return;

    this.#watchExitIntent();
  }

  disconnectedCallback() {
    this.#intent.abort();
    this.#abort.abort();
    window.clearInterval(this.#timer);
  }

  #claimedAt() {
    const claimed = Number(read(CLAIMED_KEY));
    if (!claimed) return 0;
    return Date.now() - claimed < this.minutes * 60 * 1000 ? claimed : 0;
  }

  #open() {
    if (this.dialog.open) return;
    write(SEEN_KEY, String(Date.now()));
    this.dialog.showModal();
  }

  #watchExitIntent() {
    const { signal } = this.#intent;
    const armDelay = Date.now() + 6000;
    const trigger = () => {
      if (Date.now() < armDelay) return;
      if (document.querySelector('dialog[open]')) return;
      this.#open();
      this.#intent.abort();
    };

    document.documentElement.addEventListener(
      'mouseleave',
      (event) => {
        if (event.clientY <= 0) trigger();
      },
      { signal }
    );

    let maxDepth = 0;
    let lastY = window.scrollY;
    let lastTime = performance.now();
    window.addEventListener(
      'scroll',
      () => {
        const y = window.scrollY;
        const now = performance.now();
        const height = document.documentElement.scrollHeight - window.innerHeight;
        if (height > 0) maxDepth = Math.max(maxDepth, y / height);
        const speed = (lastY - y) / Math.max(now - lastTime, 1);
        if (maxDepth > 0.35 && speed > 1.6 && window.matchMedia('(pointer: coarse)').matches) trigger();
        lastY = y;
        lastTime = now;
      },
      { signal, passive: true }
    );
  }

  #showSuccess() {
    if (this.formStep) this.formStep.hidden = true;
    if (this.successStep) this.successStep.hidden = false;
    if (this.reminder) this.reminder.hidden = false;
    this.#tick();
    this.#timer = window.setInterval(() => this.#tick(), 1000);
  }

  #tick() {
    const claimed = Number(read(CLAIMED_KEY)) || Date.now();
    const left = claimed + this.minutes * 60 * 1000 - Date.now();
    if (left <= 0) {
      window.clearInterval(this.#timer);
      if (this.reminder) this.reminder.hidden = true;
      this.querySelectorAll('[data-countdown-wrap]').forEach((el) => {
        el.hidden = true;
      });
      return;
    }
    const h = Math.floor(left / 3600000);
    const m = Math.floor((left % 3600000) / 60000);
    const s = Math.floor((left % 60000) / 1000);
    const mmss = `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
    const text = h > 0 ? `${String(h).padStart(2, '0')}:${mmss}` : mmss;
    this.querySelectorAll('[data-countdown]').forEach((el) => {
      el.textContent = text;
    });
  }

  async #copy(button) {
    try {
      await navigator.clipboard.writeText(this.dataset.code);
      button.dataset.copied = '';
      window.setTimeout(() => delete button.dataset.copied, 2000);
    } catch {
      /* clipboard blocked: the code stays visible to copy by hand */
    }
  }
}

if (!customElements.get('s-exit-popup')) customElements.define('s-exit-popup', ExitPopup);
